import styled from "@emotion/styled";
import React, { useEffect, useRef, useState } from "react";

import {
  SENSOR_RAMP_GRADIENT,
  SENSOR_SCALE_MAX,
  SENSOR_SCALE_MIN,
} from "@screens/TracksScreen/trackFilter";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import Switch from "@components/Switch";
import { useLang, useSpeed } from "@src/hooks/useLang";

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 3px 0;
`;

/** Thumb width; the ramp is inset by half of it on each side so a thumb's
 *  centre sits exactly over the color it selects. */
export const THUMB = 12;

export const RangeWrap = styled.div`
  position: relative;
  height: 22px;
  margin-top: 8px;
  touch-action: none;
`;

export const Ramp = styled.div`
  position: absolute;
  left: ${THUMB / 2}px;
  right: ${THUMB / 2}px;
  top: 5px;
  height: 12px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  overflow: hidden;
`;

/** Fades the part of the ramp the range excludes. */
export const Shade = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  background: repeating-linear-gradient(135deg, rgba(255, 255, 255, 0.85) 0 3px, rgba(255, 255, 255, 0.55) 3px 5px);
`;

/**
 * Two native range inputs stacked on one track. Only the thumbs take the
 * pointer, so each handle drags on its own; being real inputs they also work
 * with the keyboard and with touch for free.
 */
export const RangeInput = styled.input`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 22px;
  margin: 0;
  background: none;
  pointer-events: none;
  -webkit-appearance: none;
  appearance: none;
  &:focus {
    outline: none;
  }
  &::-webkit-slider-runnable-track {
    height: 22px;
    background: transparent;
  }
  &::-moz-range-track {
    height: 22px;
    background: transparent;
  }
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    pointer-events: auto;
    width: ${THUMB}px;
    height: 22px;
    border-radius: 3px;
    background: #fff;
    border: 2px solid #494c5b;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    cursor: ew-resize;
  }
  &::-moz-range-thumb {
    pointer-events: auto;
    width: ${THUMB - 4}px;
    height: 18px;
    border-radius: 3px;
    background: #fff;
    border: 2px solid #494c5b;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    cursor: ew-resize;
  }
  &:focus-visible::-webkit-slider-thumb {
    border-color: #68ad18;
  }
  &:focus-visible::-moz-range-thumb {
    border-color: #68ad18;
  }
`;

const RangeLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2px;
`;

const SpeedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
`;

const SpeedInput = styled.input`
  width: 56px;
  border: 1px solid #e0e2e7;
  border-radius: 4px;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  padding: 3px 6px;
  &:focus {
    outline: none;
    border-color: #68ad18;
  }
`;

const fmt = (v: number): string => String(Math.round(v * 10) / 10);

/**
 * Redrawing every swath on each pointer move (or each keystroke) would stall
 * the map on a season of points, so a change is applied once input pauses this
 * long. Releasing a handle, Enter and blur apply at once.
 */
const APPLY_DELAY_MS = 150;

export type TrackFilterControlsProps = {
  /** Show the color range: only meaningful when the map draws the sensor value. */
  colorApplies: boolean;
  colorOn: boolean;
  /** Kept sensor range on the 0–100 scale; null = that end of the scale. */
  sensorMin: number | null;
  sensorMax: number | null;
  onColorToggle: (on: boolean) => void;
  onSensorRange: (min: number, max: number) => void;
  speedOn: boolean;
  /** km/h; null = no bound on that side. */
  speedMinKmh: number | null;
  speedMaxKmh: number | null;
  onSpeedToggle: (on: boolean) => void;
  onSpeedRange: (minKmh: number | null, maxKmh: number | null) => void;
  /** Hide headland turns, by each machine's analytics turn gate. */
  removeTurns: boolean;
  onRemoveTurnsToggle: (on: boolean) => void;
};

/**
 * "Filter by color", "Filter by speed" and "Remove turns" — one control for both track maps
 * (Tracks screen and Fields → tracks), so the two read and behave the same.
 *
 * Purely a display filter on points already loaded — nothing here refetches.
 * Speeds are shown in the user's unit (mph for acre users) and passed in km/h.
 */
const TrackFilterControls: React.FC<TrackFilterControlsProps> = (props) => {
  const lang = useLang();
  const f = (lang.fields ?? {}) as any;
  const speed = useSpeed();

  // ── Color range ──
  // The handles follow the pointer from local state; the map gets the value
  // once dragging pauses (or on release).
  const [range, setRange] = useState<[number, number]>([
    props.sensorMin ?? SENSOR_SCALE_MIN,
    props.sensorMax ?? SENSOR_SCALE_MAX,
  ]);
  const rangeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRange = useRef<[number, number] | null>(null);

  // Follow the parent when it changes from elsewhere, unless a drag is
  // mid-flight (that would yank the handle back under the pointer).
  useEffect(() => {
    if (pendingRange.current) return;
    setRange([props.sensorMin ?? SENSOR_SCALE_MIN, props.sensorMax ?? SENSOR_SCALE_MAX]);
  }, [props.sensorMin, props.sensorMax]);

  const flushRange = () => {
    if (rangeTimer.current) clearTimeout(rangeTimer.current);
    rangeTimer.current = null;
    const next = pendingRange.current;
    pendingRange.current = null;
    if (next) props.onSensorRange(next[0], next[1]);
  };

  const moveRange = (next: [number, number]) => {
    setRange(next);
    pendingRange.current = next;
    if (rangeTimer.current) clearTimeout(rangeTimer.current);
    rangeTimer.current = setTimeout(flushRange, APPLY_DELAY_MS);
  };

  const [lo, hi] = range;
  const pct = (v: number) => ((v - SENSOR_SCALE_MIN) / (SENSOR_SCALE_MAX - SENSOR_SCALE_MIN)) * 100;
  // With both handles parked at the top of the scale the upper input would
  // cover the lower one and the range could never open again; lift the lower.
  const loOnTop = lo > (SENSOR_SCALE_MIN + SENSOR_SCALE_MAX) / 2 && lo === hi;

  // ── Speed ──
  // Text being typed; null = show the committed value. Every edit — a typed
  // digit or a click on the spinner arrows — is applied once input pauses, so
  // the map follows without the user having to click away.
  const [draftMin, setDraftMin] = useState<string | null>(null);
  const [draftMax, setDraftMax] = useState<string | null>(null);
  const speedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const shown = (kmh: number | null) => (kmh == null ? "" : fmt(speed.fromKmh(kmh)));
  const minText = draftMin ?? shown(props.speedMinKmh);
  const maxText = draftMax ?? shown(props.speedMaxKmh);

  const applySpeed = (minT: string, maxT: string) => {
    if (speedTimer.current) clearTimeout(speedTimer.current);
    speedTimer.current = null;
    const parse = (t: string): number | null => {
      const n = Number(t.replace(",", "."));
      return t.trim() === "" || !Number.isFinite(n) || n < 0 ? null : speed.toKmh(n);
    };
    let min = parse(minT);
    let max = parse(maxT);
    if (min != null && max != null && min > max) [min, max] = [max, min];
    props.onSpeedRange(min, max);
  };

  // Applied but left as typed: rewriting the box mid-edit (normalising "1." or
  // swapping a min that briefly exceeds the max) would fight the user.
  const editSpeed = (minT: string, maxT: string) => {
    setDraftMin(minT);
    setDraftMax(maxT);
    if (speedTimer.current) clearTimeout(speedTimer.current);
    speedTimer.current = setTimeout(() => applySpeed(minT, maxT), APPLY_DELAY_MS);
  };

  // Leaving the box applies at once and shows the value as it was understood.
  const finishSpeed = () => {
    applySpeed(minText, maxText);
    setDraftMin(null);
    setDraftMax(null);
  };

  useEffect(
    () => () => {
      if (rangeTimer.current) clearTimeout(rangeTimer.current);
      if (speedTimer.current) clearTimeout(speedTimer.current);
    },
    []
  );

  return (
    <>
      {props.colorApplies && (
        <>
          <ToggleRow>
            <Text size={12} weight={500}>
              {f.tracksFilterColor ?? "Filter by color"}
            </Text>
            <Switch
              on={props.colorOn}
              onToggle={() => props.onColorToggle(!props.colorOn)}
              ariaLabel={f.tracksFilterColor ?? "Filter by color"}
            />
          </ToggleRow>
          {props.colorOn && (
            <>
              <RangeWrap onPointerUp={flushRange} onKeyUp={flushRange} onTouchEnd={flushRange}>
                <Ramp style={{ background: SENSOR_RAMP_GRADIENT }}>
                  <Shade style={{ left: 0, width: `${pct(lo)}%` }} />
                  <Shade style={{ left: `${pct(hi)}%`, right: 0 }} />
                </Ramp>
                <RangeInput
                  type="range"
                  min={SENSOR_SCALE_MIN}
                  max={SENSOR_SCALE_MAX}
                  step={1}
                  value={lo}
                  style={loOnTop ? { zIndex: 2 } : undefined}
                  aria-label={f.tracksColorRangeMin ?? "Lowest color shown"}
                  onChange={(e) => moveRange([Math.min(Number(e.target.value), hi), hi])}
                />
                <RangeInput
                  type="range"
                  min={SENSOR_SCALE_MIN}
                  max={SENSOR_SCALE_MAX}
                  step={1}
                  value={hi}
                  aria-label={f.tracksColorRangeMax ?? "Highest color shown"}
                  onChange={(e) => moveRange([lo, Math.max(Number(e.target.value), lo)])}
                />
              </RangeWrap>
              <RangeLabels>
                <Text size={11} color="#8c8f9b" fitContent>
                  {lo}
                </Text>
                <Text size={11} color="#8c8f9b" fitContent>
                  {hi}
                </Text>
              </RangeLabels>
              <SizedBox height={2} />
              <Text size={11} color="#8c8f9b">
                {f.tracksFilterColorRangeHint ?? "Drag the handles to show only part of the color scale."}
              </Text>
            </>
          )}
          <SizedBox height={4} />
        </>
      )}

      <ToggleRow>
        <Text size={12} weight={500}>
          {f.tracksFilterSpeed ?? "Filter by speed"}
        </Text>
        <Switch
          on={props.speedOn}
          onToggle={() => props.onSpeedToggle(!props.speedOn)}
          ariaLabel={f.tracksFilterSpeed ?? "Filter by speed"}
        />
      </ToggleRow>
      {props.speedOn && (
        <SpeedRow>
          <SpeedInput
            type="number"
            min={0}
            step="0.5"
            aria-label={f.tracksSpeedMin ?? "Min"}
            placeholder={f.tracksSpeedMin ?? "Min"}
            value={minText}
            onChange={(e) => editSpeed(e.target.value, maxText)}
            onBlur={finishSpeed}
            onKeyDown={(e) => e.key === "Enter" && finishSpeed()}
          />
          <Text size={12} color="#8c8f9b" fitContent>
            –
          </Text>
          <SpeedInput
            type="number"
            min={0}
            step="0.5"
            aria-label={f.tracksSpeedMax ?? "Max"}
            placeholder={f.tracksSpeedMax ?? "Max"}
            value={maxText}
            onChange={(e) => editSpeed(minText, e.target.value)}
            onBlur={finishSpeed}
            onKeyDown={(e) => e.key === "Enter" && finishSpeed()}
          />
          <Text size={12} color="#8c8f9b" fitContent>
            {speed.label}
          </Text>
        </SpeedRow>
      )}

      <SizedBox height={4} />
      <ToggleRow>
        <Text size={12} weight={500}>
          {f.tracksRemoveTurns ?? "Remove turns"}
        </Text>
        <Switch
          on={props.removeTurns}
          onToggle={() => props.onRemoveTurnsToggle(!props.removeTurns)}
          ariaLabel={f.tracksRemoveTurns ?? "Remove turns"}
        />
      </ToggleRow>
      {props.removeTurns && (
        <Text size={11} color="#8c8f9b">
          {f.tracksRemoveTurnsHint ??
            "Hides headland turns — the same turns analytics leaves out of harvested area."}
        </Text>
      )}
    </>
  );
};

export default TrackFilterControls;
